import { versionApi } from '@/api/client'
import { useNotifications } from '@/composables/useNotifications'
import { t } from '@/i18n'
import type { GetVersionPolicy200Response } from '@/openapi'
import { router } from '@/router'
import type { Device } from '@/types/device'

import { isSemverLower } from './semver'

type VersionPolicy = GetVersionPolicy200Response

function notifyOldAndroidVersion(deviceInfo: Device, isDataphone: boolean, config: VersionPolicy) {
  if (
    deviceInfo.platform === 'android' &&
    deviceInfo.nativeVersion &&
    isSemverLower(deviceInfo.nativeVersion, config.android.warningVersion) &&
    !isDataphone
  ) {
    const notification = useNotifications()
    notification.notifyError({
      id: 'update-app',
      title: t('notifications.update-apk.title'),
      description: t('notifications.update-apk.description'),
      important: true,
      onAction: () => {
        window.open('https://play.google.com/store/apps/details?id=app.last.pos', '_blank')
      }
    })
  }
}

function notifyDeprecatedAndroidVersion(
  deviceInfo: Device,
  isDataphone: boolean,
  config: VersionPolicy
) {
  if (
    deviceInfo.platform === 'android' &&
    deviceInfo.nativeVersion &&
    isSemverLower(deviceInfo.nativeVersion, config.android.deprecatedVersion) &&
    !isDataphone
  ) {
    router.push({ name: 'updateDevice' })
  }
}

function notifyOldIOSWarning(deviceInfo: Device, config: VersionPolicy) {
  if (
    deviceInfo.platform === 'ios' &&
    deviceInfo.nativeVersion &&
    isSemverLower(deviceInfo.nativeVersion, config.ios.warningVersion)
  ) {
    const notification = useNotifications()
    notification.notifyError({
      id: 'update-app',
      title: t('notifications.update-apk.title'),
      description: t('notifications.update-apk.description'),
      important: true,
      onAction: () => {
        window.open('https://apps.apple.com/es/app/last-app/id1481474529', '_blank')
      }
    })
  }
}

function notifyDeprecatedIOSVersion(deviceInfo: Device, config: VersionPolicy) {
  if (
    deviceInfo.platform === 'ios' &&
    deviceInfo.nativeVersion &&
    isSemverLower(deviceInfo.nativeVersion, config.ios.deprecatedVersion)
  ) {
    router.push({ name: 'updateDevice' })
  }
}

function notifyOldVersionWebView(deviceInfo: Device, config: VersionPolicy) {
  if (
    deviceInfo.platform === 'android' &&
    parseFloat(deviceInfo.webviewVersion) < config.android.webviewWarningVersion
  ) {
    const notification = useNotifications()
    notification.notifyError({
      id: 'update-webview',
      title: t('notifications.update-webview.title'),
      description: t('notifications.update-webview.description'),
      important: true,
      onAction: () => {
        window.open(
          'https://play.google.com/store/apps/details?id=com.google.android.webview',
          '_blank'
        )
      }
    })
  }
}

function handleDeprecatedAppVersion(device: Device, config: VersionPolicy) {
  if (isSemverLower(device.appVersion, config.app.deprecatedVersion)) {
    router.push({ name: 'forceUpdate' })
  }
}

export async function checkVersions(device: Device, isDataphone = false) {
  const config = await versionApi.getVersionPolicy()

  handleDeprecatedAppVersion(device, config)
  notifyOldAndroidVersion(device, isDataphone, config)
  notifyOldVersionWebView(device, config)
  notifyOldIOSWarning(device, config)
  notifyDeprecatedIOSVersion(device, config)
  notifyDeprecatedAndroidVersion(device, isDataphone, config)
}

export type { VersionPolicy }
